import React, { useCallback, useMemo, MouseEvent } from 'react';
import Tooltip from 'rc-tooltip';
import { isNull, noop } from 'lodash/fp';
import 'rc-tooltip/assets/bootstrap_white.css';

import { formatDate } from '../../../../utils/luxon';
import { TimelineInstance, toggleDeleteConfirmationModal, toggleEditMetaDataModal } from '../store';
import styles from './HistoricalInstances.module.scss';
import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { Bin, Pencil } from '../../../shared/icons';
import { getOriginalDocument } from '../../../documents/my-documents/store';
import { Icon } from '../../../shared/icon/Icon';
import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { getUserRole } from '../../../auth/login/store';
import { adminRoles } from '../../../constants/permittedRoles';
import { DocumentType } from '../../../documents/store/types';

interface ContentProps {
    documentDescription?: string;
    documentType?: string;
    username?: string;
    executedDate: string;
    modifiedDate: string;
    width: number;
    showDelete: boolean;
    showEdit: boolean;
    isLegacy: boolean;
    deleteAmendment?: (event: MouseEvent<HTMLButtonElement>) => void;
    editMetadata: (event: MouseEvent<HTMLButtonElement>) => void;
}

interface InstanceTooltipProps {
    datasetInstance: TimelineInstance | null;
    children: JSX.Element;
    width?: number;
}

const { french, white, lightGrey } = styles;

const Content: React.FC<ContentProps> = ({ documentDescription = 'No document Added', username = 'Unknown', executedDate, modifiedDate, documentType = 'N/A', width, deleteAmendment = noop, showDelete, showEdit, editMetadata, isLegacy }) => (
    <div style={{ maxWidth: `${width - 18}px` }} className={styles.overlayWrapper}>
        <div className={styles.contentDescription}>{documentDescription}</div>
        <div className={styles.contentType}>Document Type: {capitaliseStartLetter(documentType)}</div>
        <div className={styles.contentExecutedDate}>Date of Execution: {executedDate}</div>
        <div className={styles.contentModifiedDate}>Last Modified: {modifiedDate}</div>
        <div className={styles.contentUsername}>Last Modified By: {username}</div>
        {isLegacy && <div className={styles.isLegacy}>LEGACY DOCUMENT</div>}
        {showEdit &&
            <button className={styles.editMetadataWrapper} onClick={editMetadata}>
                <div>Edit Metadata</div>
                <Icon icon={Pencil} fontSize={16} />
            </button>
        }
        {showDelete &&
            <button className={styles.deleteAmendmentWrapper} onClick={deleteAmendment}>
                <div>Delete {capitaliseStartLetter(documentType)}</div>
                <Icon icon={Bin} fontSize={16} />
            </button>
        }
    </div>
);

export const InstanceTooltip: React.FC<InstanceTooltipProps> = ({ datasetInstance, children, width = 300 }) => {
    const dispatch = useAppDispatch();
    const userRole = useAppSelector(getUserRole);
    const originalDocument = useAppSelector(getOriginalDocument);
    const tooltipStyles = {
        backgroundColor: datasetInstance && datasetInstance.isLegacy ? lightGrey : white,
        color: french,
        borderColor: french,
        borderRadius: '5px',
        minHeight: '10px',
        display: 'flex'
    };

    const overlayStyle = {
        maxWidth: `${width}px`,
        width: 'fit-content'
    };

    const instance = useMemo(() => {
        if (datasetInstance) {
            const { username, executedDate, createdDate, documentDescription, documentType, isLegacy, datedAsOfDate } = datasetInstance;
            return { username, executedDate: formatDate(executedDate), modifiedDate: formatDate(createdDate), datedAsOfDate: !isNull(datedAsOfDate) ? formatDate(datedAsOfDate) : null, documentDescription, documentType, isLegacy };
        }
        return { username: 'Unknown', executedDate: 'Unknown', datedAsOfDate: 'Unknown', modifiedDate: 'Unknown', documentDescription: 'Unknown', documentType: 'Unknown', isLegacy: false };
    }, [datasetInstance]);

    const { username, executedDate, modifiedDate, documentDescription, documentType, isLegacy } = instance;

    const isAdmin = useMemo(() => adminRoles.includes(userRole!), [userRole]);
    const showDelete = useMemo(() => documentType !== DocumentType.ORIGINAL && isAdmin && !isLegacy, [documentType, isAdmin, isLegacy]);
    const showEdit = useMemo(() => isAdmin && !isLegacy, [isAdmin, isLegacy]);

    const deleteAmendment = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        dispatch(toggleDeleteConfirmationModal({ documentId: datasetInstance!.documentId, documentDescription, datasetInstanceId: datasetInstance!.datasetInstanceId! }));
    }, [dispatch, datasetInstance, documentDescription]);

    const editMetadata = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (!isNull(datasetInstance) && !isNull(originalDocument)) {
            const { entityA, entityB, isAgency } = originalDocument;
            dispatch(toggleEditMetaDataModal({ ...datasetInstance, entityA, entityB, isAgency }));
        }
    }, [dispatch, datasetInstance, originalDocument]);

    if (!datasetInstance || datasetInstance && !datasetInstance.datasetInstanceId) {
        return children;
    }

    return (
        <Tooltip
            placement='bottom'
            overlay={<Content
                documentDescription={documentDescription}
                username={username}
                executedDate={executedDate}
                modifiedDate={modifiedDate}
                documentType={documentType}
                width={width}
                showDelete={showDelete}
                showEdit={showEdit}
                deleteAmendment={deleteAmendment}
                editMetadata={editMetadata}
                isLegacy={isLegacy}
            />}
            trigger='hover'
            overlayInnerStyle={tooltipStyles}
            overlayStyle={overlayStyle}
        >
            {children}
        </Tooltip>
    );
};
